import React, { useState } from 'react';
import {
  Drawer,
  DrawerHeader,
  DrawerBody,
  Box,
  Heading,
  Text,
  Button,
  Avatar,
  Alert,
  Badge,
  useToast,
} from '@razorpay/blade/components';
import { useMutation, useQueryClient } from 'react-query';
import employeeBankAccountApis from 'api/employeeBankAccount';
import { MISSING_REPORT_LIST, MISSING_REPORT_SUMMARY } from 'api/missingInformationReport';
import { AppError } from 'utils/AppError';
import { parseError } from 'utils/Api';
import useHasPermission from 'utils/useHasPermission';
import { BladeTooltipWrapper } from 'components/ui/BladeTooltipWrapper/BladeTooltipWrapper';
import { NameMismatchRejectModal } from './NameMismatchRejectModal';
import { DetailRow } from './DetailRow';
import { NameMismatchDetailsDrawerProps } from '../types';
import permissions from 'constants/permissions';

export const NameMismatchDetailsDrawer = ({
  selectedAccount,
  onClose,
}: NameMismatchDetailsDrawerProps) => {
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const toast = useToast();
  const queryClient = useQueryClient();
  const hasChangesApprovalPermission = useHasPermission([
    permissions.EMPLOYEE_DATA_CHANGES_APPROVAL,
  ]);

  const invalidateReportQueries = () => {
    queryClient.invalidateQueries({ queryKey: [MISSING_REPORT_SUMMARY] });
    queryClient.invalidateQueries({ queryKey: [MISSING_REPORT_LIST] });
  };

  const { mutate: approveNameMismatch, isLoading: isApproving } = useMutation({
    mutationFn: employeeBankAccountApis.approveNameMismatch,
    onSuccess: () => {
      toast.show({
        content: 'Bank account approved successfully',
        color: 'positive',
      });
      invalidateReportQueries();
      onClose();
    },
    onError: (error: typeof AppError) => {
      toast.show({
        content: parseError(error),
        color: 'negative',
      });
    },
  });

  const contactLine = selectedAccount
    ? [selectedAccount.email, selectedAccount.phone].filter(Boolean).join(' • ')
    : '';

  return (
    <>
      <Drawer
        isOpen={!!selectedAccount}
        onDismiss={onClose}
        accessibilityLabel={`name mismatch details for ${selectedAccount?.name}`}>
        <DrawerHeader color="notice" title="Employee">
          {selectedAccount ? (
            <Box display="flex" flexDirection="column">
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap="spacing.3"
                marginBottom="spacing.6">
                <Avatar name={selectedAccount.name} size="large" />
                <Heading size="medium" weight="semibold" textAlign="center">
                  {selectedAccount.name}
                </Heading>
                <Badge color="notice" emphasis="subtle">
                  Name Mismatch
                </Badge>
              </Box>

              <Box display="flex" gap="spacing.4" marginBottom="spacing.5">
                <Box
                  width="45%"
                  borderLeftColor="surface.border.gray.muted"
                  borderLeftWidth="thicker"
                  paddingLeft="spacing.4">
                  <Text size="xsmall" color="surface.text.gray.muted">
                    Employee Name
                  </Text>
                  <Text size="small">{selectedAccount.name}</Text>
                </Box>
                <Box
                  borderLeftColor="surface.border.gray.muted"
                  borderLeftWidth="thicker"
                  paddingLeft="spacing.4">
                  <Text size="xsmall" color="surface.text.gray.muted">
                    Fetched from Bank
                  </Text>
                  <Text size="small">{selectedAccount.account_holder_name}</Text>
                </Box>
              </Box>

              <Alert
                isDismissible={false}
                color="notice"
                emphasis="subtle"
                description={`The bank record name (${selectedAccount.account_holder_name}) does not match our employee record. Approve only if you have verified this is a joint/family account.`}
                marginBottom="spacing.5"
              />

              <Box display="flex" gap="spacing.3" width="100%">
                <BladeTooltipWrapper
                  content={
                    hasChangesApprovalPermission
                      ? ''
                      : 'You do not have permission to perform this action'
                  }
                  placement="top">
                  <Button
                    variant="secondary"
                    size="medium"
                    color="negative"
                    isFullWidth
                    isDisabled={!hasChangesApprovalPermission}
                    onClick={() => setIsRejectModalOpen(true)}>
                    Reject
                  </Button>
                </BladeTooltipWrapper>
                <BladeTooltipWrapper
                  content={
                    hasChangesApprovalPermission
                      ? ''
                      : 'You do not have permission to perform this action'
                  }
                  placement="top">
                  <Button
                    variant="primary"
                    size="medium"
                    isFullWidth
                    isDisabled={!hasChangesApprovalPermission || isApproving}
                    onClick={() =>
                      approveNameMismatch({ bankAccountId: selectedAccount.bank_account_id })
                    }>
                    Approve
                  </Button>
                </BladeTooltipWrapper>
              </Box>

              {contactLine && (
                <Text
                  color="surface.text.gray.muted"
                  size="small"
                  textAlign="center"
                  marginTop="spacing.5">
                  {contactLine}
                </Text>
              )}
            </Box>
          ) : undefined}
        </DrawerHeader>
        <DrawerBody>
          {selectedAccount && (
            <Box display="flex" flexDirection="column">
              <Heading size="small" weight="semibold" marginBottom="spacing.4">
                Account Details
              </Heading>
              <DetailRow label="Account Number">
                <Text size="medium">{selectedAccount.account_number || '-'}</Text>
              </DetailRow>
              <DetailRow label="IFSC Code">
                <Text size="medium">{selectedAccount.ifsc || '-'}</Text>
              </DetailRow>
              <DetailRow label="Account Holder Name">
                <Text size="medium">{selectedAccount.account_holder_name || '-'}</Text>
              </DetailRow>
            </Box>
          )}
        </DrawerBody>
      </Drawer>
      <NameMismatchRejectModal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        onRejectSuccess={onClose}
        selectedAccount={selectedAccount}
      />
    </>
  );
};
